//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { createContext, ReactNode, useContext, useState } from 'react';

const PROGRESS_COMPLETE_VALUE = 1;

export enum UploadEvent {
  CANCELED = 'CANCEL',
  COMPLETED = 'COMPLETED',
  CLEAN = 'CLEAN',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  STARTED = 'STARTED',
}

export interface UrlImport {
  fileName?: string;
}

export interface Upload {
  completed?: boolean;
  error?: string;
  file?: File;
  id: string;
  isUrlImport?: boolean;
  resultId?: string;
  progress?: number;
  urlImport?: UrlImport;
  xhr?: XMLHttpRequest;
}

export interface UploadContextProps {
  getCompletedUploads: () => Upload[];
  onUploadEvent: (type: UploadEvent, status: Upload) => void;
  uploads: Upload[];
}

export interface UploadContextProviderProps {
  children: ReactNode;
}

export const UploadContext = createContext<UploadContextProps>({
  getCompletedUploads: () => [],
  onUploadEvent: () => ({}),
  uploads: [],
});

export const useUploadContext = () => useContext(UploadContext);

export const UploadContextProvider = ({
  children,
}: UploadContextProviderProps) => {
  const [uploads, setUploads] = useState<Upload[]>([]);

  const updateUploads = (update: Upload) => {
    setUploads((uploads) =>
      uploads.map((upload) =>
        upload.id === update.id ? { ...upload, ...update } : upload,
      ),
    );
  };

  const getCompletedUploads = () => {
    return uploads.filter((upload) => upload.completed);
  };

  const onUploadEvent = (event: UploadEvent, upload: Upload) => {
    switch (event) {
      case UploadEvent.STARTED: {
        setUploads((uploads) => [...uploads, upload]);
        break;
      }
      case UploadEvent.IN_PROGRESS: {
        updateUploads(upload);
        break;
      }
      case UploadEvent.COMPLETED: {
        upload.completed = true;
        upload.progress = PROGRESS_COMPLETE_VALUE;
        updateUploads(upload);
        break;
      }
      case UploadEvent.FAILED: {
        upload.progress = PROGRESS_COMPLETE_VALUE;
        updateUploads(upload);
        break;
      }
      case UploadEvent.CLEAN:
      case UploadEvent.CANCELED: {
        setUploads((uploads) =>
          uploads.filter((item) => item.id !== upload.id),
        );
        break;
      }
    }
  };

  return (
    <UploadContext.Provider
      value={{ getCompletedUploads, onUploadEvent, uploads }}
    >
      {children}
    </UploadContext.Provider>
  );
};
